import type { ComponentProps } from "react";
import { Fragment } from "react";

import { ServiceImageBlock } from "./service-image-block";

import type { ServiceEntity } from "~/entities/service";
import { createImageUrl } from "~/entities/service";
import { useConfig } from "~/shared/config";
import { createUrl } from "~/shared/http";
import { formatCurrency, Tk, useTranslation2 } from "~/shared/i18n";
import { QUESTION_MARK } from "~/shared/str";
import { Dd, Dl, Dt } from "~/shared/ui/description-list";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/shared/ui/dialog";

type Props = ComponentProps<typeof Dialog> & {
  service: ServiceEntity.Schema;
  triggerProps: Required<Pick<ComponentProps<typeof DialogTrigger>, "children">> &
    Omit<ComponentProps<typeof DialogTrigger>, "children">;
};

function ServiceDetailsDialog({
  service: { name, description, price, imageId, features },
  triggerProps,
  ...props
}: Props) {
  const {
    postproc: { tc, ts },
  } = useTranslation2();
  const {
    backendApp: { protocol, address, port, apiBaseUrl },
  } = useConfig();

  return (
    <Dialog {...props}>
      <DialogTrigger {...triggerProps} />
      <DialogContent className="flex max-h-9/10 flex-col">
        <DialogHeader>
          <DialogTitle className="flex justify-between pr-4">
            <span>{name}</span>
            <span>{formatCurrency(price)}</span>
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 overflow-auto">
          <ServiceImageBlock
            imageProps={{
              src: createImageUrl(
                createUrl({
                  protocol,
                  address,
                  port,
                  baseUrl: apiBaseUrl,
                }),
                imageId,
              ),
            }}
          />
          <div className="flex flex-col gap-2">
            <h4 className="text-xl">{ts(QUESTION_MARK)(Tk.WHAT_IS_INCLUDED_IN_THE_SERVICE)}</h4>
            {features.length > 0 ? (
              <Dl gap="0.5rem">
                {features.map(({ id, name, description }) => (
                  <Fragment key={id}>
                    <Dt>{name}</Dt>
                    <Dd>{description}</Dd>
                  </Fragment>
                ))}
              </Dl>
            ) : (
              <p className="text-center">{tc(Tk.NO_INFORMATION_PROVIDED_AT_THIS_TIME)}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { ServiceDetailsDialog };
